import { useRef, useCallback } from "react"

/**
 * Attaches a native DOM "input" event listener to the given ref element.
 * When Playwright dispatches synthetic input events (page.fill / page.type),
 * React's synthetic onChange may not fire. This hook catches the native event
 * and calls onNativeInput with the current value.
 *
 * Safe for user typing: React onChange fires first; if the native listener
 * also fires, the dispatchEvent call is idempotent (same value).
 */
export function useNativeInputSync(
  onNativeInput: (value: string) => void,
): React.RefCallback<HTMLInputElement | HTMLTextAreaElement> {
  const callbackRef = useRef(onNativeInput)
  callbackRef.current = onNativeInput

  const refCallback = useCallback((node: HTMLInputElement | HTMLTextAreaElement | null) => {
    if (!node) return
    const handler = (e: Event) => {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement
      callbackRef.current(target.value)
    }
    node.addEventListener("input", handler)
    // Store cleanup on the node for GC
    ;(node as any).__cogoCleanup = () => node.removeEventListener("input", handler)
  }, [])

  return refCallback
}
